<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type PartialTestData = App.Students.Data.PartialTestData;
type TestData = App.Students.Data.TestData;

import { formatDate } from '@/utils/date';

import DataRequestDialog from '@/domains/data-request/components/DataRequestDialog.vue';
import TestDataRequestForm from './TestDataRequestForm.vue';

const props = defineProps<{
  apCourses: EnumData[];
  customTests: App.Configuration.Data.CustomTestData[];
  dataRequest?: DataRequestData;
  labelMap: Record<string, string>;
  scoreMap: Record<string, Record<string, { rules?: string[]; step?: number }>>;
  test: TestData;
  types: EnumData[];
}>();

const dataRequestAttributes = computed(
  () => props.dataRequest?.attributes as PartialTestData | undefined
);

const typeOptions = computed(() => [
  ...props.types,
  {
    value: 'custom-test',
    label: 'Custom test',
    description: null
  }
]);

const isCustomTest = computed(() => getType()?.value === 'custom-test');

const formUrl = computed(() => {
  if (props.dataRequest?.status.value === 'pending') {
    return route('student.data-requests.test.update', {
      dataRequest: props.dataRequest.id
    });
  }
  return route('student.data-requests.test.store');
});

const form = useForm({
  url: formUrl.value,
  method: 'POST',
  preserveState: false,
  fields: {
    test: {
      type: getType(),
      subject: getSubject(),
      test: getTest(),
      date:
        formatDate(dataRequestAttributes.value?.date ?? props.test?.date, {
          format: 'yyyy-MM-dd'
        }) ?? '',
      is_complete: dataRequestAttributes.value?.is_complete ?? props.test?.is_complete ?? false,
      scores: getScoresMap(),
      ...(isCustomTest.value ? getScoresMap() : {})
    },
    user_comment: props.dataRequest?.user_comment ?? ''
  },
  transform: (fields) => ({
    ...fields,
    requestable_id: props.test?.id,
    test: {
      type:
        fields.test.type?.value === 'custom-test'
          ? fields.test.test?.value
          : fields.test.type?.value,
      subject: fields.test.subject?.value,
      test: fields.test.test?.value,
      date: fields.test.date,
      is_complete: fields.test.is_complete,
      scores: fields.test.scores,
      score: fields.test.score,
      score2: fields.test.score2,
      score3: fields.test.score3,
      score4: fields.test.score4,
      score5: fields.test.score5
    }
  })
});

function getType() {
  const preferred = dataRequestAttributes.value?.type?.value ?? props.test?.type?.value ?? null;
  const matched = typeOptions.value.find((option) => option.value === preferred);
  if (matched) {
    return matched;
  }
  return typeOptions.value.find((option) => option.value === 'custom-test');
}

function getSubject() {
  if (dataRequestAttributes.value?.type?.value === 'ap-course') {
    const subjectScore = dataRequestAttributes.value?.scores?.find(
      (score) => score.name === 'subject'
    )?.score;
    return props.apCourses.find((subject) => subject.value === subjectScore);
  }
  if (props.test?.type?.value === 'ap-course') {
    const subjectScore = props.test?.scores?.find((score) => score.name === 'subject')?.score;
    return props.apCourses.find((subject) => subject.value === subjectScore);
  }
  return null;
}

function getTest() {
  const preferred = dataRequestAttributes.value?.type?.value ?? props.test?.type?.value;
  const isKnownType = !!props.types.find((type) => type.value === preferred);
  if (!isKnownType && preferred) {
    return buildSelectableCustomTest(props.customTests.find((test) => test.name === preferred));
  }
  return null;
}

function getScoresMap() {
  const fromDataRequest = dataRequestAttributes.value?.scores?.reduce(
    (carry: Record<string, number>, score: { name: string; score: number | null | undefined }) => ({
      ...carry,
      [score.name]: (score.score ?? undefined) as unknown as number
    }),
    {}
  );

  if (fromDataRequest && Object.keys(fromDataRequest).length > 0) {
    return fromDataRequest;
  }

  return props.test?.scores?.reduce(
    (carry: Record<string, number>, score: App.Students.Data.TestScoreData) => ({
      ...carry,
      [score.name]: score.score
    }),
    {}
  );
}
</script>

<template>
  <DataRequestDialog dataRequestType="Exams" :form="form">
    <template #form>
      <TestDataRequestForm
        :dataRequest="dataRequest"
        :form="form"
        :test="test"
        :types="typeOptions"
        :scoreMap="scoreMap"
        :labelMap="labelMap"
        :customTests="customTests"
        :apCourses="apCourses"
      />
    </template>
  </DataRequestDialog>
</template>
